import {
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
	parseDate,
} from "@sembark-travel/datetime-utils"
import {
	Alert,
	Badge,
	Box,
	Button,
	Card,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	RelativeTime,
	Select,
	Spinner,
	Stack,
	Text,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	DatePickerField,
	ErrorMessage,
	FieldArray,
	Form,
	getEditablePhoneNumber,
	PhoneInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useCallback, useEffect, useState } from "react"
import config from "../../../../../config"
import { Email, IPhoneNumber, PhoneNumber } from "../../../../../Contacts"
import { useParams } from "../../../../../router-utils"
import { ILocation } from "../../../../../Locations"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import useSWR from "swr"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { DocumentFileInputField } from "../../../../../DocTypes"
import { AxiosError } from "axios"
import * as Validator from "yup"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
const adultSalutations = ["Mr.", "Mrs.", "Ms."]
const childSalutations = ["Master", "Miss"]

type TGuest = {
	is_document_complete: boolean
	total_required: number
	pending_count: number
	completed_count: number
	document_list: {
		name: string
		uid: string
		document_id?: number
		document_type_id: number
		is_uploaded: boolean
		media_url?: string
		file?: File
	}[]
	pending_list: {
		id: number
		name: string
	}[]
	email: string
	id: number | null
	name: string
	dob?: string
	age_during_trip?: number
	type: "adult" | "child"
	salutation?: "Mr." | "Mrs." | "Ms." | "Master" | "Miss"
	nationality: ILocation["country"]
	name_without_salutation: string
	phone_numbers: Array<IPhoneNumber<TGuest>>
	is_primary: 1 | 0
}

type TGuestDocument = {
	trip: TGuestDocumentForm["trip"]
	tourists: TGuest[]
	brand: TGuestDocumentForm["brand"]
	countries: TGuestDocumentForm["countries"]
	has_all_documents: boolean
}

type TGuestDocumentForm = {
	trip: {
		id: number
		name: string
		pax_count: number
		start_date: string
		start_date_local: string
		start_date_formated: string
		link_expires_at: string
	}
	tourists: {
		tourist: TGuest
		isExpanded: boolean
	}[]
	countries: {
		id: number
		name: string
		phone_code: string
		short_name: string
	}[]
	documents_counts: {
		total: number
		uploaded: number
		pending: number
	}
	brand?: {
		address: string
		email: string
		logo_url: string
		name: string
		phone_numbers: Array<IPhoneNumber<TGuestDocumentForm["brand"]>>
	}
	consent: 1 | 0
	has_all_documents: boolean
}

const guestDocumentFormSchema = Validator.object({
	tourists: Validator.array().of(
		Validator.object({
			tourist: Validator.object({
				name_without_salutation: Validator.string()
					.trim()
					.required("Tourist name is required"),

				type: Validator.string()
					.oneOf(["adult", "child"])
					.required("Tourist type is required"),

				age_during_trip: Validator.number()
					.transform((value, originalValue) =>
						originalValue === "" ||
						originalValue === null ||
						originalValue === undefined
							? undefined
							: value
					)
					.when("type", {
						is: "child",
						then: (schema) =>
							schema
								.required("Age is required for child")
								.min(1, "Age cannot be less than 1"),
						otherwise: (schema) => schema.notRequired(),
					}),

				document_list: Validator.array().of(
					Validator.object({
						uid: Validator.string().trim().required("Document ID is required"),
					})
				),
			}),
		})
	),
})

function GuestDocumnetXHR(xhr: XHRInstance) {
	return {
		async get(tripId: string, expires: string, signature: string) {
			return xhr
				.get<TGuestDocument>(
					`${config.apiBaseUrl}/form/tourist/documents/${tripId}`,
					{
						params: { expires, signature },
					}
				)
				.then(({ data }) => {
					const start_date_formated = localOrUtcTimestampToLocalDate(
						data.trip.start_date_local,
						data.trip.start_date
					)
					const formatedData: TGuestDocumentForm = {
						...data,
						trip: {
							...data.trip,
							start_date_formated: start_date_formated.toISOString(),
						},
						tourists: data.tourists.map((tourist) => ({
							tourist,
							isExpanded: false,
						})),
						documents_counts: {
							total: data.tourists.reduce(
								(acc, guest) => guest.total_required + acc,
								0
							),
							uploaded: data.tourists.reduce(
								(acc, guest) => guest.completed_count + acc,
								0
							),
							pending: data.tourists.reduce(
								(acc, guest) => guest.pending_count + acc,
								0
							),
						},
						consent: 0,
					}
					return formatedData
				})
		},
		async update({
			tripId,
			expires,
			signature,
			values,
		}: {
			tripId: string
			expires: string
			signature: string
			values: TGuestDocumentForm
		}) {
			const formData = new FormData()
			values.tourists.forEach((touristObj, i) => {
				const t = touristObj.tourist
				if (t.id) formData.append(`data[${i}][id]`, String(t.id))
				if (t.type) formData.append(`data[${i}][type]`, t.type)
				if (t.name_without_salutation)
					formData.append(`data[${i}][name]`, t.name_without_salutation)
				if (t.salutation)
					formData.append(`data[${i}][salutation]`, t.salutation)
				if (t.age_during_trip)
					formData.append(
						`data[${i}][age_during_trip]`,
						String(t.age_during_trip)
					)
				if (t.nationality)
					formData.append(
						`data[${i}][nationality_id]`,
						String(t.nationality.id)
					)
				if (t.email) formData.append(`data[${i}][email]`, t.email)
				if (t.dob)
					formData.append(
						`data[${i}][dob]`,
						t.dob ? formatDate(t.dob, "YYYY-MM-DD") : ""
					)

				// Handle phone_numbers
				if (t.phone_numbers && Array.isArray(t.phone_numbers)) {
					t.phone_numbers.forEach((phone, j) => {
						if (phone) {
							formData.append(
								`data[${i}][phone_numbers][${j}][number]`,
								phone.number
							)
							formData.append(
								`data[${i}][phone_numbers][${j}][country_code]`,
								phone.country_code
							)
							formData.append(
								`data[${i}][phone_numbers][${j}][is_primary]`,
								String(phone.is_primary)
							)
						}
					})
				}

				// Handle documents
				if (t.document_list && Array.isArray(t.document_list)) {
					t.document_list.forEach(
						({ document_type_id, document_id, uid, file }, k) => {
							if (document_type_id)
								formData.append(
									`data[${i}][documents][${k}][document_type_id]`,
									String(document_type_id)
								)
							formData.append(`data[${i}][documents][${k}][uid]`, uid)
							formData.append(
								`data[${i}][documents][${k}][document_id]`,
								String(document_id ?? null)
							)
							if (file)
								formData.append(`data[${i}][documents][${k}][file]`, file)
						}
					)
				}
			})
			return xhr
				.post<{
					message: string
				}>(`${config.apiBaseUrl}/form/tourist/documents/${tripId}`, formData, {
					params: { expires, signature },
				})
				.then((resp) => resp.data)
		},
	}
}

function useGuestDocument(tripId: string, expires: string, signature: string) {
	const xhr = useXHR()
	const {
		data: initialValues,
		mutate,
		error,
		isLoading,
		isValidating,
	} = useSWR(
		`form/tourist/documents/${tripId}?expires=${expires}&signature=${signature}`,
		() => GuestDocumnetXHR(xhr).get(tripId, expires, signature),
		{ isPaused: () => false }
	)

	return {
		initialValues,
		mutate,
		error,
		isLoading,
		isValidating,
	}
}

export default function TouristDocumentUploadPage() {
	const { xs, sm, md } = useBreakpoints()
	const { tripId } = useParams("/form/tourist/documents/:tripId")
	const searchParams = new URLSearchParams(window.location.search)
	const expires = searchParams.get("expires") ?? ""
	const signature = searchParams.get("signature") ?? ""

	const { mutate, isLoading, error, initialValues } = useGuestDocument(
		tripId,
		expires,
		signature
	)

	function onRefresh() {
		mutate()
	}
	const primaryTourist = initialValues?.tourists.find(
		(t) => t.tourist.is_primary
	)?.tourist

	const hasAllDocuments = initialValues?.has_all_documents
	if (hasAllDocuments)
		return (
			<AllDocumentsUploaded tripId={tripId} primaryTourist={primaryTourist} />
		)
	if (isLoading) return <Loading />
	if (error) return <LinkExpired error={error} />

	return (
		<Stack justifyContent="center" alignItems="center" paddingY="8">
			<Stack
				bgColor="default"
				paddingX="4"
				paddingY="2"
				gap="6"
				rounded="lg"
				borderTopColor="muted"
				style={{
					width: xs || sm || md ? "100%" : "80%",
					borderTopWidth: "10px",
					borderTopLeftRadius: "16px",
					borderTopRightRadius: "16px",
				}}
			>
				<Branding initialValues={initialValues} />
				<Stack gap="4">
					<Stack gap="2">
						<Text fontSize="3xl" fontWeight="semibold">
							Upload Document(s)
						</Text>
						<Text fontSize="md" color="muted">
							Please upload all the required guest documents below for a
							hassle-free travel experience.
						</Text>
					</Stack>
					<PendingProgress initialValues={initialValues} />
					<UploadInstructions />
				</Stack>
				<GuestDocumentForm
					tripStartDateLocal={initialValues?.trip.start_date_formated}
					initialValues={initialValues}
					onRefresh={onRefresh}
					tripId={tripId}
					expires={expires}
					signature={signature}
				/>
			</Stack>
		</Stack>
	)
}

function Branding({ initialValues }: { initialValues?: TGuestDocumentForm }) {
	if (!initialValues) return null
	const { brand } = initialValues
	if (!brand) return null
	const { name, address, logo_url, email, phone_numbers } = brand
	return (
		<Inline
			bgColor="subtle"
			gap="4"
			paddingX="4"
			paddingY="3"
			rounded="lg"
			flexWrap="wrap"
		>
			<Box
				as="img"
				alt={name}
				src={logo_url}
				style={{ width: "7.5rem", height: "7.5rem", objectFit: "cover" }}
			/>
			<Stack gap="2">
				<Text fontSize="2xl" fontWeight="semibold">
					{name}
				</Text>
				<Text fontSize="base" color="muted">
					{address}
				</Text>
				<Inline gap="2" color="primary" flexWrap="wrap">
					{joinAttributes(
						phone_numbers ? <PhoneNumber value={phone_numbers} /> : null,
						email ? <Email value={email} /> : null
					)}
				</Inline>
			</Stack>
		</Inline>
	)
}

function PendingProgress({
	initialValues,
}: {
	initialValues?: TGuestDocumentForm
}) {
	if (!initialValues) return null

	const { uploaded, total, pending } = initialValues.documents_counts
	const allUploaded = uploaded === total && total > 0

	return (
		<Stack gap="2">
			{allUploaded ? (
				<Text color="muted" fontWeight="semibold">
					All required documents are uploaded.
				</Text>
			) : (
				<Inline color="muted" gap="2" flexWrap="wrap">
					<Text>Total Docs: {total}</Text>
					<Text>•</Text>
					<Text>Uploaded: {uploaded}</Text>
					<Text>•</Text>
					<Text>Pending: {pending}</Text>
				</Inline>
			)}
			<Stack
				bgColor="subtle"
				height="2"
				rounded="full"
				width="full"
				overflow="hidden"
			>
				<Box
					bgColor="primary_emphasis"
					rounded="md"
					style={{
						width: total > 0 ? `${(uploaded / total) * 100}%` : "0%",
						transition: "width 0.4s",
					}}
					height="full"
				/>
			</Stack>
		</Stack>
	)
}

function GuestDocumentForm({
	initialValues: propInitialValues,
	tripStartDateLocal,
	onRefresh,
	tripId,
	expires,
	signature,
}: {
	tripStartDateLocal?: string
	initialValues?: TGuestDocumentForm
	onRefresh: () => void
	tripId: string
	expires: string
	signature: string
}) {
	const xhr = useXHR()
	const [formKey, setFormKey] = useState(0)
	const initialValues = propInitialValues
		? {
				...propInitialValues,
				tourists: propInitialValues.tourists.map((touristObj) => ({
					...touristObj,
					tourist: {
						...touristObj.tourist,
						phone_numbers: touristObj.tourist.phone_numbers.map((phone) => ({
							...phone,
							...getEditablePhoneNumber(phone.phone_number, phone.country_code),
						})),
					},
				})),
			}
		: undefined
	return (
		<Form<TGuestDocumentForm>
			key={formKey}
			initialValues={initialValues}
			mutators={{ ...arrayMutators }}
			validate={validateFormValues(guestDocumentFormSchema)}
			onSubmit={withServerErrors(async (values) => {
				if (!values.consent) {
					alert(
						"Please provide your consent before submitting the form. Select the consent checkbox to proceed."
					)
					return
				}
				const response = await GuestDocumnetXHR(xhr).update({
					values,
					tripId,
					expires,
					signature,
				})
				showSnackbar(response.message)
				onRefresh()
				setFormKey((prev) => prev + 1)
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="4">
						<Text color={"danger"}>
							Note : This link will expire{" "}
							<RelativeTime value={initialValues?.trip.link_expires_at} />.
							Please make sure to complete your document submission before the
							link expires.
						</Text>
						<TouristList tripStartDateLocal={tripStartDateLocal} />
						<Consent />
						<Box>
							<Divider sm />
							<Stack gap="4">
								<SubmissionError />
								<Box>
									<Button disabled={submitting} level="primary" type="submit">
										{submitting ? "Submiting..." : "Submit"}
									</Button>
								</Box>
							</Stack>
						</Box>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function TouristList({ tripStartDateLocal }: { tripStartDateLocal?: string }) {
	const { value: tourists } =
		useFieldValue<TGuestDocumentForm["tourists"]>("tourists")
	const { value: paxCount } = useFieldValue<number>("trip.pax_count")
	const primaryTourist = tourists.find((t) => t.tourist.is_primary)?.tourist

	const documentList = primaryTourist?.document_list.map((doc) => ({
		document_id: undefined,
		document_type_id: doc.document_type_id,
		is_uploaded: false,
		media_url: undefined,
		name: doc.name,
		uid: "-",
	}))

	return (
		<FieldArray<TGuestDocumentForm["tourists"][number]> name="tourists">
			{({ fields: tourists }) => (
				<Stack gap="4">
					{tourists.map((name, idx) => (
						<Stack
							key={name}
							borderWidth="1"
							borderColor="muted"
							bgColor="subtle"
							rounded="md"
						>
							<TouristCard
								fieldName={name}
								onRemove={() => tourists.remove(idx)}
							/>
							<TouristAccordion
								fieldName={name}
								tripStartDateLocal={tripStartDateLocal}
							/>
						</Stack>
					))}
					{paxCount > (tourists.length || 0) ? (
						<Box>
							<Button
								onClick={() => {
									tourists.push({
										tourist: {
											id: null,
											is_document_complete: false,
											total_required: documentList?.length || 0,
											pending_count: documentList?.length || 0,
											completed_count: 0,
											document_list: documentList || [],
											pending_list: [],
											email: "",
											name: "",
											dob: undefined,
											age_during_trip: undefined,
											type: "adult",
											salutation: undefined,
											nationality:
												primaryTourist && primaryTourist.nationality
													? primaryTourist.nationality
													: undefined,
											name_without_salutation: "",
											phone_numbers: [],
											is_primary: 0,
										},
										isExpanded: true,
									})
								}}
							>
								<Icons.Plus /> Add Tourist
							</Button>
						</Box>
					) : null}
				</Stack>
			)}
		</FieldArray>
	)
}

function useTouristExpand(fieldName: string) {
	const { value: isExpanded, onChange } = useFieldValue<boolean>(
		`${fieldName}.isExpanded`
	)
	function toggle() {
		onChange(!isExpanded)
	}

	return { isExpanded, toggle }
}

function TouristCard({
	fieldName,
	onRemove,
}: {
	fieldName: string
	onRemove: () => void
}) {
	const { value: data } =
		useFieldValue<TGuestDocumentForm["tourists"][number]>(fieldName)
	const { tourist } = data
	const { document_list } = tourist
	const { toggle, isExpanded } = useTouristExpand(fieldName)
	const uploadedDocuments = document_list.filter(
		(doc) => doc.is_uploaded
	).length
	return (
		<Inline
			justifyContent="between"
			paddingX="4"
			paddingY="2"
			onClick={toggle}
			cursor="pointer"
			style={{ userSelect: "none" }}
			flexWrap="wrap"
			gap="2"
		>
			<TouristHeader tourist={tourist} />
			<Inline gap="2" justifyContent="center" alignItems="center">
				<Text>
					{uploadedDocuments}/{document_list.length} Documents
				</Text>
				<Button inline>
					{isExpanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
				</Button>
				{!tourist.id ? (
					<Button
						inline
						onClick={(e) => {
							e.stopPropagation()
							onRemove()
						}}
					>
						<Icons.Trash color={"danger"} />
					</Button>
				) : null}
			</Inline>
		</Inline>
	)
}

function TouristHeader({
	tourist,
}: {
	tourist: TGuestDocumentForm["tourists"][number]["tourist"]
}) {
	const {
		type,
		age_during_trip,
		nationality,
		name_without_salutation,
		salutation,
		is_primary,
	} = tourist

	const validPhoneNumbers =
		tourist.phone_numbers?.filter((phone) => Boolean(phone.number)) ?? []

	return (
		<Stack gap="1" flexWrap="wrap">
			<Inline gap="2" alignItems="center">
				<Box>
					{type === "adult" ? (
						<Icons.UserRound color="primary" />
					) : (
						<Icons.Child color="default" />
					)}
				</Box>
				<Inline gap={"1"} alignItems={"center"}>
					<Text fontWeight="semibold" fontSize="md">
						{salutation}
					</Text>

					<Text fontWeight="semibold" fontSize="md">
						{name_without_salutation}
					</Text>

					<Text fontWeight="semibold" fontSize="md">
						{age_during_trip ? `(${age_during_trip}y)` : null}
					</Text>
					{is_primary ? (
						<Badge success size="sm">
							Primary
						</Badge>
					) : null}
				</Inline>
			</Inline>
			<Inline gap="1" alignItems="center" color="muted" flexWrap="wrap">
				{joinAttributes(
					age_during_trip ? `${age_during_trip}y` : null,
					nationality ? <Text>{nationality.nationality}</Text> : null,
					validPhoneNumbers.length ? (
						<PhoneNumber value={validPhoneNumbers} />
					) : null,
					tourist.email ? <Email value={tourist.email} /> : null
				)}
			</Inline>
		</Stack>
	)
}

function TouristAccordion({
	fieldName,
	tripStartDateLocal,
}: {
	fieldName: string
	tripStartDateLocal?: string
}) {
	const { value: data } = useFieldValue<TGuestDocumentForm["tourists"][number]>(
		`${fieldName}`
	)
	const { isExpanded, tourist } = data
	if (!isExpanded) return null

	const isPrimary = Boolean(tourist.is_primary)

	return (
		<Stack
			gap="6"
			bgColor="default"
			roundedBottom="md"
			paddingX="4"
			paddingY="4"
			borderTopWidth="1"
			borderTopColor="muted"
		>
			{isPrimary ? null : (
				<>
					<TouristBasicDetails
						fieldName={fieldName}
						tripStartDateLocal={tripStartDateLocal}
						tourist={tourist}
					/>
					<Divider sm marginY="0" />
				</>
			)}
			<TouristDocuments fieldName={fieldName} />
		</Stack>
	)
}

function TouristBasicDetails({
	fieldName,
	tripStartDateLocal,
	tourist,
}: {
	fieldName: string
	tripStartDateLocal?: string
	tourist: TGuest
}) {
	const form = useForm()
	const handleDobChange = useCallback(
		(dob: Date | undefined) => {
			form.change(`${fieldName}.tourist.dob` as never, dob as never)
			if (dob && tripStartDateLocal) {
				let calculatedAge: number | undefined = undefined
				try {
					calculatedAge = getDiff(
						parseDate(tripStartDateLocal),
						parseDate(dob),
						"years"
					)
				} catch (e) {
					calculatedAge = undefined
				}
				form.change(
					`${fieldName}.tourist.age_during_trip` as never,
					calculatedAge as never
				)
			} else {
				form.change(`${fieldName}.tourist.age_during_trip` as never, undefined)
			}
		},
		[fieldName, form, tripStartDateLocal]
	)
	const { value: countries } =
		useFieldValue<TGuestDocumentForm["countries"]>("countries")
	const { type, dob, email } = tourist
	return (
		<Grid noGutters gap="4">
			<Col xs={5} sm={5} md={3} lg={2} xl={2}>
				<Stack gap={"1"}>
					<SelectInputField
						name={`${fieldName}.tourist.type`}
						label="Type"
						disabled={tourist.id !== null}
						required
						placeholder="Select Type"
						onChange={(e) => {
							const value = e.currentTarget.value
							form.batch(() => {
								form.change(`${fieldName}.tourist.type`, value)
								form.change(`${fieldName}.tourist.salutation`, "")
							})
						}}
					>
						<option value="adult">Adult</option>
						<option value="child">Child</option>
					</SelectInputField>
					<Inline alignItems="center" gap="1" padding="1">
						<Icons.Info color="muted" fontSize="xs" />{" "}
						<Text fontSize="xs" color="muted">
							Type cannot be changes once the tourist is created.
						</Text>
					</Inline>
				</Stack>
			</Col>
			<Col xs={6} sm={5} md={3} lg={2} xl={2}>
				<SelectField
					select={Select}
					placeholder={type === "adult" ? "e.g. Mr." : "e.g. Master"}
					name={`${fieldName}.tourist.salutation`}
					label="Salutation"
					options={type === "adult" ? adultSalutations : childSalutations}
				/>
			</Col>
			<Col xs={12} sm={5} md={5} lg={4} xl={4}>
				<TextInputField
					name={`${fieldName}.tourist.name_without_salutation`}
					label="Name"
					required
					placeholder="e.g. Guest Name"
				/>
			</Col>

			<Col xs={5} sm={6} md={3} lg={3} xl={3}>
				<DatePickerField
					name={`${fieldName}.tourist.dob`}
					label="Date of Birth"
					dateFormat="DD MMM, YYYY"
					placeholder="e.g. 12/01/1990"
					onChange={handleDobChange}
					max={new Date()}
				/>
			</Col>

			<Col xs={6} sm={5} md={3} lg={2} xl={2}>
				<TextInputField
					name={`${fieldName}.tourist.age_during_trip`}
					label={`Age`}
					type="number"
					placeholder="e.g. 25"
					disabled={Boolean(dob)}
					min={0}
				/>
			</Col>

			<Col xs={12} sm={5} md={5} lg={3} xl={3}>
				<SelectField
					name={`${fieldName}.tourist.nationality`}
					label="Nationality"
					placeholder="e.g. Indian"
					labelKey="nationality"
					optionRenderer={({
						option,
					}: {
						option: TGuestDocumentForm["countries"][number]
					}) => {
						return <Text>{option.name}</Text>
					}}
					options={countries}
					select={Select}
				/>
			</Col>

			<Col xs={"auto"} sm={"auto"} md={"auto"} lg={"auto"} xl={"auto"}>
				<Inline gap="2" flexWrap="wrap">
					<PhoneInputField
						name={`${fieldName}.tourist.phone_numbers`}
						label="Phone Number(s)"
						multi
						required
						fullWidth={true}
					/>
					<Component initialState={!!email}>
						{({ state, setState }) => (
							<>
								{state ? (
									<TextInputField
										name={`${fieldName}.tourist.email`}
										label="Email"
										type="email"
										placeholder="e.g. tourist@example.com"
									/>
								) : (
									<Stack gap="1">
										<Box>&nbsp;</Box>
										<Box>
											<Button
												onClick={() => setState(true)}
												title="Click to add Email Address"
											>
												<Icons.Mail />
											</Button>
										</Box>
									</Stack>
								)}
							</>
						)}
					</Component>
				</Inline>
			</Col>
		</Grid>
	)
}

function TouristDocuments({ fieldName }: { fieldName: string }) {
	return (
		<Stack gap="4">
			<FieldArray name={`${fieldName}.tourist.document_list`}>
				{({ fields: documents }) =>
					documents.map((documentFieldName) => (
						<DocumentItem
							fieldName={fieldName}
							documentFieldName={documentFieldName}
							key={documentFieldName}
						/>
					))
				}
			</FieldArray>
		</Stack>
	)
}

function DocumentItem({
	documentFieldName,
	fieldName,
}: {
	documentFieldName: string
	fieldName: string
}) {
	const { value: documentData } =
		useFieldValue<
			TGuestDocumentForm["tourists"][number]["tourist"]["document_list"][number]
		>(documentFieldName)
	const { value: tourist } = useFieldValue<TGuest>(`${fieldName}.tourist`)
	const { name, is_uploaded, file } = documentData

	let documentStatus: React.ReactNode
	if (is_uploaded) {
		documentStatus = <Badge success>Already Uploaded</Badge>
	} else if (file) {
		documentStatus = `${file?.name}`
	} else {
		documentStatus = null
	}

	return (
		<Grid
			key={name}
			bgColor="subtle"
			rounded="md"
			paddingX="4"
			paddingY="2"
			justifyContent="between"
			alignItems="center"
			gap="2"
		>
			<Col xs={12} md={3}>
				<Stack gap="1">
					<Text>{name}</Text>
					<Text fontSize={{ xs: "sm", md: "base" }}>{documentStatus}</Text>
					<ErrorMessage name={`${documentFieldName}.file`} />
				</Stack>
			</Col>

			{!is_uploaded || !tourist.id ? (
				<Col xs={12} md={4}>
					<TextInputField
						name={`${documentFieldName}.uid`}
						style={{ width: "300px" }}
						placeholder="Document ID"
					/>
				</Col>
			) : null}
			<Col xs={12} md={4}>
				<DocumentUploader data={documentData} name={documentFieldName} />
			</Col>
		</Grid>
	)
}

function DocumentUploader({
	name,
	data,
}: {
	name: string
	data: TGuestDocumentForm["tourists"][number]["tourist"]["document_list"][number]
}) {
	const form = useForm()
	const [isOpen, open, close] = useDialog()
	const { is_uploaded, file, media_url } = data
	const hasDocument = Boolean(file || media_url)
	const { xs } = useBreakpoints()
	return (
		<Inline gap="4" flexWrap="wrap" justifyContent={xs ? "start" : "end"}>
			{!is_uploaded ? (
				<>
					<Box
						as="span"
						style={{
							display: "none",
						}}
					>
						<DocumentFileInputField
							id={`${name}.fileInput`}
							name={`${name}.file`}
						/>
					</Box>
					<Button
						status="primary"
						level="secondary"
						onClick={() => {
							const input = document.getElementById(`${name}.fileInput`)
							if (input) input?.click()
						}}
					>
						<Icons.Upload /> {file ? "Replace" : "Upload"}
					</Button>
				</>
			) : null}
			{hasDocument ? (
				<Button onClick={open}>
					<Icons.ArrowExpand title="Preview" /> Preview
				</Button>
			) : null}
			{file ? (
				<Button
					inline
					onClick={() => {
						form.change(`${name}.file`, null)
					}}
				>
					<Icons.Cancel size="5" />
				</Button>
			) : null}
			{isOpen ? (
				<PreviewDocument
					close={close}
					isOpen={isOpen}
					file={file}
					url={media_url}
				/>
			) : null}
		</Inline>
	)
}

function UploadInstructions() {
	const instructions = [
		"The documents are not password-protected, blurry, cropped, obstructed by a hand or any object, or affected by glare or flash.",
		"The image should be less than 6 MB (JPG, JPEG, or PNG), or the PDF should be less than 2 MB.",
		"⁠The documents are valid for at least six months beyond your travel dates (where applicable), as required by most destinations, to help avoid denied boarding or entry.",
	]
	return (
		<Stack bgColor="subtle" paddingX="4" paddingY="2" rounded="md" gap="1">
			<Inline gap="2">
				<Text fontSize="md" fontWeight="semibold">
					<Icons.Identification size="6" />
				</Text>
				<Text fontSize="md" fontWeight="semibold">
					Before uploading, please ensure that:
				</Text>
			</Inline>
			<Stack paddingLeft="12">
				<Box as="ul" listStyleType="disc" gap="2">
					{instructions.map((insctuction, i) => (
						<Text key={i} as="li">
							{insctuction}
						</Text>
					))}
				</Box>
			</Stack>
		</Stack>
	)
}
function Consent() {
	return (
		<SwitchInputField
			name="consent"
			label={
				<Text fontWeight="normal">
					I confirm that all uploaded documents are authentic, clearly legible,
					and belong to the respective traveler(s). I consent to their use by
					the travel company solely for processing this booking/application.
				</Text>
			}
		/>
	)
}

function LinkExpired({ error }: { error?: AxiosError }) {
	const steps = [
		"Contact your service provider",
		"Ask them to regenerate the link for you",
		"Open the new link to continue",
	]
	return (
		<Stack
			alignItems="center"
			justifyContent="center"
			paddingX="4"
			paddingY="12"
		>
			<Stack width="full" maxWidth="md" gap="4">
				<Stack
					gap="2"
					boxShadow="sm"
					borderWidth="1"
					padding="8"
					bgColor="default"
					rounded="lg"
				>
					<Stack gap="4">
						<Stack
							justifyContent="center"
							alignItems="center"
							position="relative"
							marginX="auto"
							borderRadius="lg"
							bgColor="danger"
							boxShadow="lg"
							borderWidth="1"
							borderColor="danger"
							style={{
								height: "4rem",
								width: "4rem",
							}}
						>
							<Box position="relative">
								<Icons.Link color="danger" height="7" width="7" />
								<Stack
									position="absolute"
									height="5"
									width="5"
									borderRadius="full"
									bgColor="default"
									style={{
										bottom: "-0.25rem",
										right: "-0.25rem",
									}}
								>
									<Icons.CircleAlert
										color="danger"
										height="5"
										width="5"
										strokeWidth={2.5}
									/>
								</Stack>
							</Box>
						</Stack>

						<Inline justifyContent="center" position="relative">
							<Badge fullRounded danger>
								<Box
									as="span"
									display="inline"
									paddingX="1"
									opacity="70"
									size="10"
								>
									•
								</Box>
								Link Expired
							</Badge>
						</Inline>
					</Stack>
					<Heading as="h1" fontSize="xl" textAlign="center">
						This link is no longer active
					</Heading>
					<Text color="muted" fontSize="sm" textAlign="center" maxWidth="md">
						The link you tried to open has expired and can&apos;t be accessed
						anymore. Please request a new one from your service provider to
						continue.
					</Text>

					<Stack
						paddingTop="4"
						rounded="lg"
						borderWidth="1"
						padding="4"
						bgColor="subtle"
					>
						<Stack gap="2">
							{steps.map((step, index) => {
								return (
									<Inline gap="1" key={step}>
										<Inline
											color="primary"
											fontSize="sm"
											fontWeight="semibold"
											alignItems="center"
											justifyContent="center"
											bgColor="primary"
											rounded="full"
											size="6"
										>
											{index + 1}
										</Inline>
										<Text>{step}</Text>
									</Inline>
								)
							})}
						</Stack>
					</Stack>
					{error?.message ? (
						<Alert status="error" title={error.message} />
					) : null}
				</Stack>
				<Text textAlign="center" fontSize="sm" color="muted">
					For your security, links automatically expire after a set time.
				</Text>
			</Stack>
		</Stack>
	)
}

function Loading() {
	return (
		<Stack
			width="screenWidth"
			height="screenHeight"
			justifyContent="center"
			alignItems="center"
			gap="2"
		>
			<Spinner />
			<Text>Loading...</Text>
		</Stack>
	)
}

function AllDocumentsUploaded({
	tripId,
	primaryTourist,
}: {
	tripId: string
	primaryTourist?: TGuest
}) {
	return (
		<Inline
			alignItems="start"
			justifyContent="center"
			paddingY="10"
			paddingX="2"
			style={{
				background: "#d7d7d7",
				minHeight: "calc(100vh - 64px)",
			}}
		>
			<Stack
				alignItems="center"
				justifyContent="center"
				gap="6"
				paddingX="8"
				paddingY="6"
				bgColor="white"
				rounded="lg"
				boxShadow={"lg"}
			>
				<Icons.AnimatedOk size={"16"} />

				<Stack gap="2" alignItems="center">
					<Text fontSize="lg" fontWeight="semibold" textAlign="center">
						All Documents Uploaded Successfully
					</Text>

					<Text color="muted" fontSize="base" textAlign="center" maxWidth="lg">
						You’re all set! Your documents are ready for review.
					</Text>
				</Stack>
				<Stack gap={"4"} alignItems={"start"} width={"full"}>
					<Inline width={"full"} alignItems={"center"}>
						<Box
							flexGrow={"1"}
							borderTopWidth={"1"}
							borderTopColor={"emphasis"}
							style={{ borderStyle: "dashed" }}
						></Box>
						<Box paddingX={"2"}>
							<Text fontSize={"sm"} color={"muted"}>
								TRIP DETAILS
							</Text>
						</Box>
						<Box
							flexGrow={"1"}
							borderTopWidth={"1"}
							borderTopColor={"emphasis"}
							style={{ borderStyle: "dashed" }}
						></Box>
					</Inline>
					<Stack gap={"2"} alignItems={"start"} width={"full"}>
						<Inline gap={"2"}>
							<Text color={"muted"} fontSize={"base"}>
								Primary Tourist -
							</Text>
							<Text fontWeight="semibold">{primaryTourist?.name}</Text>
						</Inline>

						<Inline gap={"2"}>
							<Text color={"muted"} fontSize={"base"}>
								Trip ID -
							</Text>
							<Text fontWeight="semibold">{tripId}</Text>
						</Inline>
					</Stack>
				</Stack>
			</Stack>
		</Inline>
	)
}

export function PreviewDocument({
	file,
	url,
	isOpen,
	close,
}: {
	file?: File
	url?: string
	isOpen: boolean
	close: () => void
}) {
	const [previewURL, setPreviewURL] = useState<string | undefined>(undefined)

	useEffect(() => {
		if (file) {
			const objectURL = globalThis.URL.createObjectURL(file)
			setPreviewURL(objectURL)
			return () => globalThis.URL.revokeObjectURL(objectURL)
		} else if (url) {
			setPreviewURL(url)
		}
	}, [file, url])

	if (!previewURL) return null

	const isPDF =
		file?.type === "application/pdf" || /\.pdf(\?|#|$)/i.test(previewURL)
	const name = file?.name || previewURL.split("/").at(-1)
	return (
		<Dialog sm open={isOpen} onClose={close}>
			<Dialog.Header>
				<Dialog.Title>Document Preview</Dialog.Title>
			</Dialog.Header>
			<Dialog.Body>
				<Card bgColor="subtle" paddingX="4" paddingTop="4" paddingBottom="4">
					<Inline gap="4" alignItems="center" width="full">
						<Box width="full">
							{isPDF ? (
								<Box
									as="iframe"
									title={name}
									src={previewURL}
									width="full"
									rounded="md"
									style={{ border: "none", height: "400px" }}
								/>
							) : (
								<Box
									as="img"
									rounded="md"
									src={previewURL}
									alt={name}
									style={{
										width: "100%",
										maxHeight: "70vh",
										objectFit: "contain",
										border: "none",
									}}
								/>
							)}
						</Box>
					</Inline>
				</Card>
			</Dialog.Body>
		</Dialog>
	)
}
